// frontend/src/operator/OperatorUserManagement.js
// 運営パネル用のユーザー登録・権限編集・削除ページ (owner 専用)

import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { writeClientAudit } from '../utils/auditClient';

const OPERATOR_PATH = process.env.REACT_APP_OPERATOR_PATH || 'ops-7k3m9x2p4r8q';

const ROLE_OPTIONS = [
  { value: 'owner',  label: 'owner (全権限 / 他運営者管理可)' },
  { value: 'admin',  label: 'admin (運営操作のみ)' },
  { value: 'viewer', label: 'viewer (閲覧のみ)' },
];

export default function OperatorUserManagement() {
  const navigate = useNavigate();
  const [myUserId, setMyUserId] = useState(null);
  const [myRole, setMyRole] = useState(null);
  const [checking, setChecking] = useState(true);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // add form
  const [addForm, setAddForm] = useState({ email: '', name: '', password: '', role: 'admin' });
  const [adding, setAdding] = useState(false);

  // inline edit
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'admin' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      const uid = sess?.session?.user?.id;
      if (!uid) { setChecking(false); return; }
      setMyUserId(uid);
      const { data: me } = await supabase
        .from('superadmins')
        .select('role')
        .eq('user_id', uid)
        .maybeSingle();
      setMyRole(me?.role || null);
      setChecking(false);
    })();
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data, error: e } = await supabase
        .from('superadmins')
        .select('user_id, role, name, email, granted_at, granted_by, updated_at')
        .order('granted_at', { ascending: false });
      if (e) throw e;
      setUsers(data || []);
    } catch (e) {
      setError(`一覧取得エラー: ${e.message}`);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (myRole === 'owner') fetchAll(); }, [myRole, fetchAll]);

  if (checking) return <div style={styles.center}>確認中...</div>;
  if (!myUserId) return <Navigate to={`/${OPERATOR_PATH}/login`} replace />;
  if (myRole !== 'owner') {
    return (
      <div style={styles.page}>
        <div style={styles.center}>
          <h2 style={{ color: '#f87171' }}>アクセス権限がありません</h2>
          <p style={{ color: '#94a3b8' }}>このページは owner 権限のみアクセス可能です。 (あなたのロール: {myRole || 'なし'})</p>
          <Link to={`/${OPERATOR_PATH}`} style={styles.linkBtn}>← ダッシュボードへ</Link>
        </div>
      </div>
    );
  }

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!addForm.email || !addForm.name || !addForm.password) return setError('全項目必須');
    if (addForm.password.length < 8) return setError('パスワードは 8 文字以上');
    setAdding(true);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('operator-user-create', {
        body: {
          email: addForm.email.trim(),
          password: addForm.password,
          name: addForm.name.trim(),
          role: addForm.role,
        },
      });
      if (fnErr) throw fnErr;
      if (!data?.success) throw new Error(data?.message || '追加に失敗');
      setSuccess(`✅ ${addForm.email} を追加しました`);
      setAddForm({ email: '', name: '', password: '', role: 'admin' });
      await fetchAll();
    } catch (e) {
      setError(`追加エラー: ${e.message}`);
    } finally { setAdding(false); }
  };

  const startEdit = (u) => {
    setEditingId(u.user_id);
    setEditForm({ name: u.name || '', role: u.role });
    setError(''); setSuccess('');
  };
  const cancelEdit = () => setEditingId(null);

  const handleUpdate = async (uid) => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      const { error: e } = await supabase
        .from('superadmins')
        .update({ name: editForm.name.trim(), role: editForm.role, updated_at: new Date().toISOString() })
        .eq('user_id', uid);
      if (e) throw e;
      await writeClientAudit({
        actionType: 'update',
        targetTable: 'superadmins',
        targetId: uid,
        targetSummary: `運営者情報更新: ${editForm.name} / role=${editForm.role}`,
        changes: { name: editForm.name.trim(), role: editForm.role },
        success: true,
      });
      setSuccess('✅ 更新しました');
      setEditingId(null);
      await fetchAll();
    } catch (e) {
      await writeClientAudit({
        actionType: 'update',
        targetTable: 'superadmins',
        targetId: uid,
        targetSummary: `運営者情報更新に失敗`,
        success: false,
        errorMessage: e.message,
      });
      setError(`更新エラー: ${e.message}`);
    } finally { setSaving(false); }
  };

  const handleDelete = async (u) => {
    if (u.user_id === myUserId) return alert('自分自身は削除できません');
    if (!window.confirm(`「${u.name || u.email}」を運営者から削除しますか?\n(auth.users のアカウント自体は残ります)`)) return;
    setError(''); setSuccess('');
    try {
      const { error: e } = await supabase.from('superadmins').delete().eq('user_id', u.user_id);
      if (e) throw e;
      await writeClientAudit({
        actionType: 'delete',
        targetTable: 'superadmins',
        targetId: u.user_id,
        targetSummary: `運営者を削除: ${u.email} (${u.name || ''})`,
        success: true,
      });
      setSuccess('🗑️ 削除しました');
      await fetchAll();
    } catch (e) {
      await writeClientAudit({
        actionType: 'delete',
        targetTable: 'superadmins',
        targetId: u.user_id,
        targetSummary: `運営者削除に失敗: ${u.email}`,
        success: false,
        errorMessage: e.message,
      });
      setError(`削除エラー: ${e.message}`);
    }
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => navigate(`/${OPERATOR_PATH}`)} style={styles.backBtn}>← ダッシュボード</button>
          <h1 style={styles.title}>🔐 運営ユーザー管理</h1>
        </div>
      </header>

      <main style={styles.main}>
        {error && <div style={styles.errorBox}>{error}</div>}
        {success && <div style={styles.successBox}>{success}</div>}

        {/* 追加フォーム */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>➕ 新規運営者を追加</h2>
          <form onSubmit={handleAdd} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1.5fr auto', gap: 10, alignItems: 'end' }}>
            <label>
              <div style={styles.lbl}>メールアドレス *</div>
              <input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} required style={styles.input} />
            </label>
            <label>
              <div style={styles.lbl}>表示名 *</div>
              <input type="text" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} required style={styles.input} />
            </label>
            <label>
              <div style={styles.lbl}>初期パスワード * (8文字+)</div>
              <input type="text" value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} minLength={8} required style={styles.input} />
            </label>
            <label>
              <div style={styles.lbl}>ロール</div>
              <select value={addForm.role} onChange={(e) => setAddForm({ ...addForm, role: e.target.value })} style={styles.input}>
                {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <button type="submit" disabled={adding} style={{
              ...styles.primaryBtn,
              background: adding ? '#475569' : styles.primaryBtn.background,
              cursor: adding ? 'not-allowed' : 'pointer',
            }}>
              {adding ? '追加中...' : '追加'}
            </button>
          </form>
        </section>

        {/* 一覧 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>登録済み運営者 ({users.length})</h2>
            <button onClick={fetchAll} style={styles.reloadBtn}>↻ 再読み込み</button>
          </div>
          {loading ? (
            <div style={styles.message}>読み込み中...</div>
          ) : users.length === 0 ? (
            <div style={styles.message}>運営者がまだいません</div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tr}>
                    <th style={styles.th}>メアド / 名前</th>
                    <th style={styles.th}>ロール</th>
                    <th style={styles.th}>付与日</th>
                    <th style={styles.th}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isEdit = editingId === u.user_id;
                    const isMe = u.user_id === myUserId;
                    return (
                      <tr key={u.user_id} style={{ ...styles.tr, background: isEdit ? 'rgba(250,204,21,0.1)' : undefined }}>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 700 }}>
                            {isEdit ? (
                              <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} style={styles.input} />
                            ) : (
                              <>{u.name || '(名前未設定)'}{isMe && <span style={styles.selfBadge}>自分</span>}</>
                            )}
                          </div>
                          <div style={styles.subText}>{u.email || '-'}</div>
                        </td>
                        <td style={styles.td}>
                          {isEdit ? (
                            <select value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} style={styles.input}>
                              {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.value}</option>)}
                            </select>
                          ) : (
                            <span style={{
                              ...styles.roleBadge,
                              background: u.role === 'owner' ? '#7c2d12' : u.role === 'admin' ? '#1e40af' : '#334155',
                              color: 'white',
                            }}>{u.role}</span>
                          )}
                        </td>
                        <td style={styles.td}>{u.granted_at ? new Date(u.granted_at).toLocaleDateString('ja-JP') : '-'}</td>
                        <td style={styles.td}>
                          {isEdit ? (
                            <>
                              <button onClick={() => handleUpdate(u.user_id)} disabled={saving} style={styles.saveBtn}>保存</button>
                              <button onClick={cancelEdit} disabled={saving} style={styles.cancelBtn}>取消</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => startEdit(u)} style={styles.editBtn}>編集</button>
                              <button onClick={() => handleDelete(u)} disabled={isMe} style={{
                                ...styles.deleteBtn,
                                opacity: isMe ? 0.4 : 1,
                                cursor: isMe ? 'not-allowed' : 'pointer',
                              }}>削除</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#0f172a', color: '#e2e8f0' },
  center: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 },
  header: { background: '#1e293b', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #334155' },
  title: { margin: 0, fontSize: 20, fontWeight: 700 },
  backBtn: { padding: '6px 14px', background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  linkBtn: { padding: '6px 14px', background: '#334155', color: '#e2e8f0', textDecoration: 'none', borderRadius: 6, fontSize: 12 },
  main: { maxWidth: 1200, margin: '0 auto', padding: 24 },
  section: { background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 20, border: '1px solid #334155' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  sectionTitle: { margin: 0, fontSize: 16, fontWeight: 700 },
  reloadBtn: { padding: '4px 12px', background: '#334155', color: '#cbd5e1', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11 },
  lbl: { fontSize: 11, color: '#94a3b8', marginBottom: 4 },
  input: { width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #475569', background: '#0f172a', color: '#e2e8f0', fontSize: 13, boxSizing: 'border-box' },
  primaryBtn: { padding: '8px 16px', background: 'linear-gradient(135deg, #059669, #047857)', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, height: 34 },
  message: { padding: 24, textAlign: 'center', color: '#94a3b8' },
  errorBox: { background: 'rgba(239,68,68,0.15)', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, whiteSpace: 'pre-line' },
  successBox: { background: 'rgba(34,197,94,0.15)', color: '#86efac', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  tr: { borderBottom: '1px solid #334155' },
  th: { padding: 10, textAlign: 'left', fontWeight: 700, color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' },
  td: { padding: 10, verticalAlign: 'middle' },
  subText: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  selfBadge: { marginLeft: 8, padding: '2px 6px', background: '#3b82f6', color: 'white', fontSize: 10, borderRadius: 8 },
  roleBadge: { padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, letterSpacing: '0.5px' },
  editBtn: { padding: '4px 10px', background: '#1e40af', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', marginRight: 4 },
  deleteBtn: { padding: '4px 10px', background: '#991b1b', color: 'white', border: 'none', borderRadius: 6, fontSize: 11 },
  saveBtn: { padding: '4px 10px', background: '#059669', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', marginRight: 4 },
  cancelBtn: { padding: '4px 10px', background: '#475569', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer' },
};
